import { Icon } from '@natera/material/lib/icon'
import { Tooltip } from 'radix-ui'
import type { PropsWithChildren, ReactNode } from 'react'
import { cn } from '~/shared/lib/cn'
import { AGREEMENT_LEGEND, AGREEMENT_STYLES } from '../config'
import type { Agreement } from '../lib'
import { useDocumentPane } from '../model'

export function MaterialIcon({ name, className }: { name: string; className?: string }) {
  return (
    <Icon
      className={cn('shrink-0 leading-none', className)}
      aria-hidden
    >
      {name}
    </Icon>
  )
}

/** A titled block of the review: a heading with one line on what it shows, then a card. */
export function StorySection({
  id,
  title,
  subtitle,
  aside,
  bare = false,
  children,
}: PropsWithChildren<{
  id?: string
  title: string
  subtitle?: ReactNode
  aside?: ReactNode
  /** Content draws its own surface. */
  bare?: boolean
}>) {
  return (
    <section
      id={id}
      className="flex scroll-mt-4 flex-col gap-3"
    >
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2 px-1">
        <div className="flex max-w-[640px] flex-col gap-0.5">
          <h2 className="text-text-primary m-0 text-[17px] leading-6 font-semibold">{title}</h2>
          {subtitle && <p className="text-text-secondary m-0 text-[13px] leading-5">{subtitle}</p>}
        </div>
        {aside}
      </header>
      {bare ? (
        children
      ) : (
        <div className="border-divider bg-surface-lowest rounded-xl border">{children}</div>
      )}
    </section>
  )
}

/** An info icon with a short explanation on hover or focus. */
export function Hint({ children, className }: PropsWithChildren<{ className?: string }>) {
  return (
    <Tooltip.Provider delayDuration={150}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <button
            type="button"
            className={cn(
              'text-text-tertiary hover:text-text-secondary inline-flex cursor-help items-center border-none bg-transparent p-0 align-middle',
              className,
            )}
            aria-label="What this means"
          >
            <MaterialIcon
              name="info"
              className="text-[15px]"
            />
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className="border-divider bg-surface-lowest text-text-primary z-50 max-w-[320px] rounded-lg border px-3 py-2 text-[12px] leading-[18px] font-normal shadow-[0_4px_16px_rgba(0,0,0,0.12)]"
            sideOffset={6}
            collisionPadding={12}
          >
            {children}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  )
}

/** A small hover explanation around any element. */
export function WithTip({ tip, children }: { tip: ReactNode; children: ReactNode }) {
  if (!tip) return <>{children}</>
  return (
    <Tooltip.Provider delayDuration={250}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className="bg-surface-high-inverse text-content-highest-emphasis-inverse z-50 max-w-[280px] rounded-md px-2.5 py-1.5 text-[12px] leading-4"
            sideOffset={4}
            collisionPadding={12}
          >
            {tip}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  )
}

export function AgreementDot({
  agreement,
  className,
}: {
  agreement: Agreement
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-block size-2 shrink-0 rounded-full',
        AGREEMENT_STYLES[agreement].dot,
        className,
      )}
      aria-hidden
    />
  )
}

/** "Agrees", "Differs", … as a soft pill, with an optional explanation on hover. */
export function AgreementBadge({
  agreement,
  label,
  tip,
  size = 'sm',
}: {
  agreement: Agreement
  label?: string
  tip?: ReactNode
  size?: 'sm' | 'md'
}) {
  const style = AGREEMENT_STYLES[agreement]
  return (
    <WithTip tip={tip}>
      <span
        className={cn(
          'inline-flex shrink-0 items-center gap-1 rounded-full font-medium whitespace-nowrap',
          style.fill,
          style.text,
          size === 'md' ? 'h-7 px-2.5 text-[13px]' : 'h-6 px-2 text-[12px]',
        )}
      >
        <MaterialIcon
          name={style.icon}
          className={size === 'md' ? 'text-[16px]' : 'text-[14px]'}
        />
        {label ?? style.label}
      </span>
    </WithTip>
  )
}

export function AgreementLegend({ className }: { className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-center gap-x-4 gap-y-1', className)}>
      {AGREEMENT_LEGEND.map(({ agreement, label }) => (
        <span
          key={agreement}
          className="text-text-secondary inline-flex items-center gap-1.5 text-[12px] leading-4"
        >
          <AgreementDot agreement={agreement} />
          {label}
        </span>
      ))}
    </div>
  )
}

/** A verbatim quote from the document; clicking it finds the quote in the document text. */
export function EvidenceQuote({ text, className }: { text: string; className?: string }) {
  const { showQuote } = useDocumentPane()
  return (
    <button
      type="button"
      onClick={() => showQuote(text)}
      className={cn(
        'group border-l-swatch-yellow-60 hover:bg-swatch-yellow-50 text-text-primary flex w-full cursor-pointer items-start gap-2 rounded-r-md border-y-0 border-r-0 border-l-[3px] bg-transparent py-1 pr-2 pl-3 text-left text-[13px] leading-5 transition-colors',
        className,
      )}
      title="Find this quote in the document text"
    >
      <span className="min-w-0 flex-1 wrap-anywhere whitespace-pre-wrap italic">
        “{text.trim()}”
      </span>
      <MaterialIcon
        name="manage_search"
        className="text-text-tertiary group-hover:text-text-secondary mt-0.5 text-[16px]"
      />
    </button>
  )
}

/** A link-styled button that opens a page in the document pane. */
export function PageLink({ page, children }: PropsWithChildren<{ page: number }>) {
  const { showPage } = useDocumentPane()
  return (
    <button
      type="button"
      onClick={() => showPage(page)}
      className="text-text-link cursor-pointer border-none bg-transparent p-0 text-[12px] leading-4 hover:underline"
    >
      {children ?? `Page ${page}`}
    </button>
  )
}

export function Muted({ children, className }: PropsWithChildren<{ className?: string }>) {
  return (
    <span className={cn('text-text-secondary text-[12px] leading-4', className)}>{children}</span>
  )
}

export function Mono({ children, className }: PropsWithChildren<{ className?: string }>) {
  return <span className={cn('font-mono text-[12px] leading-4', className)}>{children}</span>
}
